import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Radio, InputNumber, Select, DatePicker, Space, Typography, Tag, Alert, Input } from 'antd';
import PropTypes from 'prop-types';
import {
  FREQ_OPTIONS,
  WEEKDAY_OPTIONS,
  MONTH_OPTIONS,
  MONTHDAY_OPTIONS,
  SETPOS_OPTIONS,
  DEFAULT_STATE,
  buildRRuleString,
  parseRRuleString,
  previewOccurrences,
} from './rruleUtils';

const { Text } = Typography;

/**
 * Bộ tạo chuỗi lặp RRULE, thay cho link mở trang /backend/event/rrule của CMS cũ.
 * Chọn xong bấm "Dùng chuỗi này" là điền thẳng vào ô Kiểu lặp — không phải sang
 * hệ thống khác rồi copy tay như trước.
 *
 * dtstart chỉ dùng để xem trước các lần lặp, không đi vào chuỗi kết quả:
 * mốc bắt đầu thật của sự kiện là trường start_time.
 */
const RRuleBuilderModal = ({ open, value, dtstart, onCancel, onOk }) => {
  const [state, setState] = useState(DEFAULT_STATE);

  // Mở modal thì nạp lại từ chuỗi hiện có, đóng rồi mở lại không giữ state cũ
  useEffect(() => {
    if (open) setState(parseRRuleString(value));
  }, [open, value]);

  const patch = (changes) => setState((prev) => ({ ...prev, ...changes }));

  const result = useMemo(() => buildRRuleString(state), [state]);
  const preview = useMemo(() => previewOccurrences(result, dtstart), [result, dtstart]);

  const unitLabel = { YEARLY: 'năm', MONTHLY: 'tháng', WEEKLY: 'tuần', DAILY: 'ngày' }[state.freq];

  return (
    <Modal
      open={open}
      title="Tạo chuỗi lặp"
      okText="Dùng chuỗi này"
      cancelText="Huỷ"
      width={720}
      onCancel={onCancel}
      onOk={() => onOk(result)}
      destroyOnClose
    >
      {/* component={false}: chỉ mượn layout/label của Form, không render thêm thẻ <form>
          lồng trong form sự kiện (Enter trong modal sẽ submit nhầm form ngoài) */}
      <Form layout="vertical" className="mt-4" component={false}>
        <Form.Item label="Tần suất" className="mb-3">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={FREQ_OPTIONS}
            value={state.freq}
            onChange={(e) => patch({ freq: e.target.value })}
          />
        </Form.Item>

        <Form.Item label="Lặp lại mỗi" className="mb-3">
          <Space>
            <InputNumber min={1} max={999} value={state.interval} onChange={(v) => patch({ interval: v || 1 })} />
            <Text type="secondary">{unitLabel}</Text>
          </Space>
        </Form.Item>

        {state.freq === 'WEEKLY' && (
          <Form.Item label="Vào các thứ" className="mb-3">
            <Select
              mode="multiple"
              allowClear
              className="w-full"
              placeholder="Chọn thứ trong tuần (bỏ trống = theo ngày bắt đầu)"
              options={WEEKDAY_OPTIONS}
              value={state.byweekday}
              onChange={(v) => patch({ byweekday: v })}
            />
          </Form.Item>
        )}

        {state.freq === 'MONTHLY' && (
          <>
            <Form.Item label="Lặp theo" className="mb-3">
              <Radio.Group value={state.monthlyMode} onChange={(e) => patch({ monthlyMode: e.target.value })}>
                <Radio value="day">Ngày trong tháng</Radio>
                <Radio value="weekday">Thứ trong tháng</Radio>
              </Radio.Group>
            </Form.Item>
            {state.monthlyMode === 'day' ? (
              <Form.Item label="Vào ngày" className="mb-3">
                <Select
                  mode="multiple"
                  allowClear
                  className="w-full"
                  placeholder="Chọn ngày (bỏ trống = theo ngày bắt đầu)"
                  options={MONTHDAY_OPTIONS}
                  value={state.bymonthday}
                  onChange={(v) => patch({ bymonthday: v })}
                />
              </Form.Item>
            ) : (
              <Form.Item label="Vào" className="mb-3">
                <Space.Compact className="w-full">
                  <Select
                    className="w-2/5"
                    options={SETPOS_OPTIONS}
                    value={state.bysetpos}
                    onChange={(v) => patch({ bysetpos: v })}
                  />
                  <Select
                    mode="multiple"
                    allowClear
                    className="w-3/5"
                    placeholder="Chọn thứ"
                    options={WEEKDAY_OPTIONS}
                    value={state.byweekday}
                    onChange={(v) => patch({ byweekday: v })}
                  />
                </Space.Compact>
              </Form.Item>
            )}
          </>
        )}

        {state.freq === 'YEARLY' && (
          <>
            <Form.Item label="Vào tháng" className="mb-3">
              <Select
                mode="multiple"
                allowClear
                className="w-full"
                placeholder="Chọn tháng (bỏ trống = theo ngày bắt đầu)"
                options={MONTH_OPTIONS}
                value={state.bymonth}
                onChange={(v) => patch({ bymonth: v })}
              />
            </Form.Item>
            <Form.Item label="Vào ngày" className="mb-3">
              <Select
                mode="multiple"
                allowClear
                className="w-full"
                placeholder="Chọn ngày (bỏ trống = theo ngày bắt đầu)"
                options={MONTHDAY_OPTIONS}
                value={state.bymonthday}
                onChange={(v) => patch({ bymonthday: v })}
              />
            </Form.Item>
          </>
        )}

        <Form.Item label="Kết thúc" className="mb-3">
          <Radio.Group value={state.endMode} onChange={(e) => patch({ endMode: e.target.value })}>
            <Radio value="never">Không giới hạn</Radio>
            <Radio value="count">Sau số lần</Radio>
            <Radio value="until">Đến ngày</Radio>
          </Radio.Group>
          {state.endMode === 'count' && (
            <div className="mt-2">
              <InputNumber
                min={1}
                max={1000}
                value={state.count}
                onChange={(v) => patch({ count: v || 1 })}
                addonAfter="lần"
              />
            </div>
          )}
          {state.endMode === 'until' && (
            <div className="mt-2">
              <DatePicker
                format="DD/MM/YYYY"
                value={state.until}
                onChange={(v) => patch({ until: v })}
                placeholder="Chọn ngày kết thúc"
              />
            </div>
          )}
        </Form.Item>

        <Form.Item label="Chuỗi kết quả" className="mb-3">
          <Input readOnly value={result} className="font-mono" />
        </Form.Item>

        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
          <Text type="secondary" className="text-xs uppercase tracking-wide font-semibold">
            Xem trước các lần lặp
          </Text>
          {preview.error ? (
            <Alert type="error" showIcon className="mt-2" message={preview.error} />
          ) : (
            <div className="mt-2">
              {preview.dates.length ? (
                <Space size={[4, 4]} wrap>
                  {preview.dates.map((d) => (
                    <Tag key={d} color="blue">
                      {d}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">Không có lần lặp nào khớp lựa chọn hiện tại.</Text>
              )}
            </div>
          )}
          <div className="mt-2">
            <Text type="secondary" className="text-xs">
              Tính từ Thời gian bắt đầu của sự kiện (bỏ trống thì lấy hôm nay). Chuỗi lặp không chứa mốc bắt đầu.
            </Text>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

RRuleBuilderModal.propTypes = {
  open: PropTypes.bool,
  value: PropTypes.string,
  dtstart: PropTypes.any,
  onCancel: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired,
};

export default RRuleBuilderModal;
